import { type SplitIds, joinIds } from '@/clientOnlyUtils/clientUtils';
import { cn } from '@poseidon/components/cn';
import { contactNameFlat } from '@poseidon/components/contactUtils';
import ContentViewedCardBodyPure from '@poseidon/components/ContentViewedCardBodyPure';
import { type ComponentProps } from 'react';

import { useRouterQuery } from '@/hooks/useRouterQuery';

import { CheckboxIconButton } from '@poseidon/components/Input2';
import { ContactCardContainer } from '@poseidon/components/ContactCardContainer';
import { type ContactCardOrgRowOrg } from '@poseidon/components/ContactCardOrgRowPure';
import { type ContactCardUser } from '~/components/ContactCard/ContactCardPure';
import { type ContactCardBodyContact } from '~/components/ContactCard/ContactCardTypes';

import { ContactCardImageWithOwner } from '@poseidon/components/ContactCardImage';

export type ContentViewedCardProps = ComponentProps<'div'> & {
  contact: ContactCardBodyContact;
  contactUser: ContactCardUser;
  authedUserLiConnected?: boolean | null;
  authedUserLiConnectedAt?: Date | null;
  interactionId: string;
  documentTitle: string;
  org: ContactCardOrgRowOrg | null;
  onToggleChecked?: (data: SplitIds, e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

export const ContentViewedCard = ({
  contact,
  contactUser,
  authedUserLiConnected,
  authedUserLiConnectedAt,
  documentTitle,
  interactionId,
  org,
  onToggleChecked,
  className,
}: ContentViewedCardProps) => {
  const [query, setQuery] = useRouterQuery();
  const isChecked =
    onToggleChecked != null && query.selectedIds?.includes(joinIds({ contactId: contact.id, interactionId }));
  const somethingChecked = (query.selectedIds?.length || 0) > 0;

  const onClick = () => {
    setQuery((o) => ({
      ...o,
      id: contact.id,
      cadenceId: undefined,
      orgId: undefined,
      stepId: undefined,
    }));
  };

  const name = contactNameFlat({ first_name: contact.firstName ?? null, last_name: contact.lastName ?? null });

  return (
    <div className={cn('group flex gap-3.5', className)}>
      {onToggleChecked && (
        <div>
          <CheckboxIconButton
            onClick={(e) => onToggleChecked({ contactId: contact.id, interactionId }, e)}
            checked={!!isChecked}
            somethingChecked={somethingChecked}
          />
        </div>
      )}

      <ContactCardContainer isChecked={isChecked} active={query.id === contact.id} onClick={onClick}>
        <ContactCardImageWithOwner
          name={name}
          firstName={contact.firstName}
          lastName={contact.lastName}
          imageSrc={contact.photoUrl}
          orgPictureUrl={org?.pictureUrl}
          showContactUser={contactUser != null}
          contactUserProfilePicture={contactUser?.profilePicture}
          contactUserLiConnected={contactUser?.liConnected}
          contactUserLiConnectedAt={contactUser?.liConnectedAt}
          contactUserName={contactUser?.name}
          contactUserEmail={contactUser?.email}
          authedUserLiConnected={authedUserLiConnected}
          authedUserLiConnectedAt={authedUserLiConnectedAt}
        />
        <ContentViewedCardBodyPure
          name={name}
          honorificTitle={contact.displayHonorific ? (contact.honorificTitle ?? undefined) : undefined}
          liOpenLink={contact.liOpenLink}
          liConnected={contactUser?.liConnected}
          disqualified={contactUser?.disqualified}
          title={contact.title}
          org={org}
          documentTitle={documentTitle}
        />
      </ContactCardContainer>
    </div>
  );
};
